import React, { useMemo, useState } from 'react';

import { Collapse, List, ListItem, ListItemIcon, ListItemText, makeStyles } from '@material-ui/core';
import ExitToAppIcon from '@material-ui/icons/ExitToApp';
import LanguageIcon from '@material-ui/icons/Language';
import clsx from 'clsx';
import { useTranslation } from 'react-i18next';
import { useMutation } from 'react-query';

import MenuSection from 'components/Layout/_components/MenuSection';
import { usersApi } from 'config/api/users/users';
import getShortLanguage from 'helpers/getShortLanguage';
import general_messages from 'messages/general_messages';
import menu_messages from 'messages/menu_messages';
import { useAuthContext } from 'reactContext/AuthContext';
import { getUnsavedChangesPromptHandler } from 'router/unsavedChangesPromptRegistry';
import useLayoutStore from 'storages/layoutStore';

import EmergencyMenu from '../../EmergencyMenu/EmergencyMenu';

const drawerWidth = 320;
const closedDrawerWidth = 80;

const useStyles = makeStyles(theme => ({
  '@keyframes fadeIn': {
    from: { opacity: 0 },
    to: { opacity: 1 },
  },
  wrapper: {
    borderTop: `2px solid ${theme.palette.common.black}`,
    flexGrow: 0,
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'stretch',
    position: 'sticky',
    bottom: '0',
    background: theme.palette.background.dark,
    width: ({ isMenuOpen }) => (isMenuOpen ? drawerWidth : closedDrawerWidth),
    transition: theme.transitions.create('width', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.down('sm')]: {
      width: () => '100%',
    },
  },
  list: {
    width: '100%',
  },
  icon: {
    color: theme.palette.text.inverted,
    minWidth: '40px',
    paddingLeft: ({ isMenuOpen }) => (isMenuOpen ? 'unset' : theme.spacing(0.75)),
    transition: theme.transitions.create('padding-left', {
      easing: theme.transitions.easing.easeIn,
      duration: theme.transitions.duration.leavingScreen,
    }),
  },
  text: {
    animationName: '$fadeIn',
    animationDuration: 300,
    '& span': {
      [theme.breakpoints.down('sm')]: {
        fontSize: '1.125rem !important',
      },
    },
  },
  languageText: {
    paddingLeft: ({ isMenuOpen }) => (isMenuOpen ? theme.spacing(2) : theme.spacing(2.8)),
  },
  item: {
    '@media (hover: hover)': {
      '&:hover': {
        color: theme.palette.secondary.light,
        '& $icon': {
          color: theme.palette.secondary.light,
        },
      },
    },
  },
  logout: {
    '@media (hover: hover)': {
      '&:hover': {
        color: theme.palette.error.light,
        '& $icon': {
          color: theme.palette.error.light,
        },
      },
    },
  },
  branch: {
    position: 'absolute',
    bottom: 0,
    opacity: '.75',
    whiteSpace: 'normal',
    fontSize: '11px',
  },
}));

const FooterSection = () => {
  const { isMenuOpen, closeMenu } = useLayoutStore();

  const { onLogout, isAuthorized } = useAuthContext();
  const { i18n, t } = useTranslation();

  const [isLangOpen, setLangOpen] = useState(false);
  const toggleLang = () => setLangOpen(prevState => !prevState);
  const langMutation = useMutation('Set language for user', usersApi.setLanguage);

  const changeLanguage = code => async () => {
    await i18n.changeLanguage(code);
    if (isAuthorized) langMutation.mutate({ language_code: code });
    setLangOpen(false);
  };

  const onLogoutClick = () => {
    const unsavedHandler = getUnsavedChangesPromptHandler();
    if (unsavedHandler) {
      unsavedHandler('', ok => {
        if (ok) {
          onLogout();
          closeMenu();
        }
      });
    } else {
      onLogout();
      closeMenu();
    }
  };

  const languages = useMemo(
    () =>
      [
        { label: t(...general_messages.languages.en), langCode: 'en' },
        { label: t(...general_messages.languages.sv), langCode: 'sv' },
      ].filter(({ langCode }) => langCode !== i18n.language),
    [i18n.language],
  );

  const styles = useStyles({ isMenuOpen });
  return (
    <MenuSection className={styles.wrapper} collapsible={false} divider={false}>
      <List className={styles.list}>
        {isAuthorized && <EmergencyMenu />}
        <ListItem button className={styles.item} onClick={toggleLang}>
          <ListItemIcon className={styles.icon}>
            <LanguageIcon />
          </ListItemIcon>
          {isMenuOpen && <ListItemText className={styles.text} primary={t(`languages>>${getShortLanguage(i18n.language)}`, 'Language')} />}
        </ListItem>
        <Collapse in={isLangOpen} timeout='auto' unmountOnExit>
          <List>
            {languages.map(({ label, langCode }) => (
              <ListItem key={langCode} button className={clsx(styles.item, styles.languageText)} onClick={changeLanguage(langCode)}>
                <ListItemText className={styles.text} primary={isMenuOpen ? label : langCode.toUpperCase()} />
              </ListItem>
            ))}
          </List>
        </Collapse>
        {isAuthorized && (
          <ListItem button className={styles.logout} data-cy='logout_button' onClick={onLogoutClick}>
            <ListItemIcon className={styles.icon}>
              <ExitToAppIcon />
            </ListItemIcon>
            {isMenuOpen && <ListItemText className={styles.text} primary={t(...menu_messages.logout)} />}
          </ListItem>
        )}
      </List>
      {isMenuOpen && <div className={styles.branch}>{process.env.REACT_APP_BRANCH}</div>}
    </MenuSection>
  );
};

export default FooterSection;
